import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { dubaiToday, hmTo12, timeOptions, WEEKDAYS, ymdShort } from "@/lib/format";
import { PageHeader } from "@/components/admin/StatusBadge";

const HALF_HOURS = timeOptions(30);
const QUARTERS = timeOptions(15);

function Card({ title, hint, children }) {
  return (
    <div className="glass rounded-3xl p-6">
      <h2 className="font-display text-lg font-semibold text-[#172033]">{title}</h2>
      {hint && <p className="text-xs text-[#697386] mt-1">{hint}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

function TimeSelect({ value, onChange, options, tid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="field h-auto" data-testid={tid}><SelectValue placeholder="Select time" /></SelectTrigger>
      <SelectContent className="max-h-72">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function NumberField({ label, value, onChange, tid, min, max }) {
  return (
    <label className="block">
      <span className="text-sm text-[#172033]">{label}</span>
      <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="field mt-2" data-testid={tid} />
    </label>
  );
}

function SpecificTimes({ s, setS }) {
  const [date, setDate] = useState(dubaiToday());
  const [slots, setSlots] = useState(null);
  const [extra, setExtra] = useState("");

  useEffect(() => {
    setSlots(null);
    api.get("/admin/slots", { params: { date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date]);

  const isBlocked = (time) => s.blocked_slots.some((b) => b.date === date && b.time === time);
  const toggle = (time) => setS((p) => ({ ...p, blocked_slots: isBlocked(time) ? p.blocked_slots.filter((b) => !(b.date === date && b.time === time)) : [...p.blocked_slots, { date, time }] }));
  const addExtra = () => {
    if (!extra || s.extra_slots.some((x) => x.date === date && x.time === extra)) return;
    setS((p) => ({ ...p, extra_slots: [...p.extra_slots, { date, time: extra }] }));
    setExtra("");
  };
  const extras = s.extra_slots.filter((x) => x.date === date);

  return (
    <Card title="Specific times" hint="Block individual times or add extra one-off slots for a chosen date. Tap a time to block or unblock it. Times shown follow your saved hours — save first if you changed hours or duration.">
      <input type="date" min={dubaiToday()} value={date} onChange={(e) => setDate(e.target.value)} className="field max-w-xs" data-testid="specific-date-input" />
      <div className="mt-4 flex flex-wrap gap-2" data-testid="specific-slots-list">
        {slots === null && <Loader2 size={16} className="animate-spin text-[#697386]" />}
        {slots?.length === 0 && <p className="text-sm text-[#697386]">No regular slots on this date.</p>}
        {slots?.map((sl) => {
          const blocked = isBlocked(sl.time);
          return (
            <button key={sl.time} onClick={() => toggle(sl.time)} disabled={sl.booked_count > 0 && !blocked} className={`rounded-xl px-3 py-2 text-xs font-semibold border transition-colors ${blocked ? "bg-slate-100 text-slate-400 line-through border-slate-200" : sl.booked_count ? "bg-[#0B1F3A] text-white border-[#0B1F3A] cursor-not-allowed" : "bg-white border-[rgba(20,35,55,0.12)] hover:border-[#0B1F3A]/40"}`} data-testid={`specific-slot-${sl.time}`}>
              {sl.label}{sl.booked_count ? " · booked" : ""}
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="sm:w-48"><TimeSelect value={extra} onChange={setExtra} options={QUARTERS} tid="extra-slot-time-select" /></div>
        <button onClick={addExtra} className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 text-sm font-medium" data-testid="extra-slot-add-button"><Plus size={14} /> Add extra slot</button>
      </div>
      {extras.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {extras.map((x) => (
            <span key={x.time} className="inline-flex items-center gap-1 rounded-full bg-[#C7A76C]/15 text-[#7A5F2E] px-3 py-1 text-xs" data-testid={`extra-slot-${x.time}`}>
              {hmTo12(x.time)}
              <button onClick={() => setS((p) => ({ ...p, extra_slots: p.extra_slots.filter((e) => e !== x) }))} aria-label="Remove extra slot"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Availability() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newBlock, setNewBlock] = useState("");
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => setLoadError(formatApiError(e)));
  }, []);

  if (loadError) return <p className="text-sm text-[#C2410C]" data-testid="availability-load-error">{loadError}</p>;
  if (!s) return <Loader2 className="animate-spin text-[#697386]" />;

  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const toggleDay = (i) => setS((p) => ({ ...p, weekdays: p.weekdays.includes(i) ? p.weekdays.filter((d) => d !== i) : [...p.weekdays, i] }));
  const addBlock = () => {
    if (newBlock && !s.blocked_dates.includes(newBlock)) setS((p) => ({ ...p, blocked_dates: [...p.blocked_dates, newBlock].sort() }));
    setNewBlock("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { timezone, ...body } = s;
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      toast.success("Availability saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-availability">
      <PageHeader title="Availability" subtitle="Rules apply in Dubai time (GST, UTC+4). Changes take effect immediately after saving.">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="availability-save-button">{saving ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}</button>
      </PageHeader>

      <Card title="Working days & hours">
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d, i) => (
            <button key={d} onClick={() => toggleDay(i)} aria-pressed={s.weekdays.includes(i)} className={`h-11 w-14 rounded-2xl text-sm font-semibold border transition-colors ${s.weekdays.includes(i) ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white border-[rgba(20,35,55,0.12)] text-[#697386]"}`} data-testid={`weekday-toggle-${d.toLowerCase()}`}>{d}</button>
          ))}
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="text-sm">Start time</span><div className="mt-2"><TimeSelect value={s.start_time} onChange={set("start_time")} options={HALF_HOURS} tid="start-time-select" /></div></label>
          <label className="block"><span className="text-sm">End time</span><div className="mt-2"><TimeSelect value={s.end_time} onChange={set("end_time")} options={HALF_HOURS} tid="end-time-select" /></div></label>
        </div>
      </Card>

      <Card title="Appointment rules">
        <div className="grid sm:grid-cols-3 gap-4">
          <NumberField label="Duration (minutes)" value={s.duration} onChange={set("duration")} min={10} max={240} tid="duration-input" />
          <NumberField label="Buffer between (minutes)" value={s.buffer} onChange={set("buffer")} min={0} max={180} tid="buffer-input" />
          <NumberField label="Capacity per slot" value={s.capacity} onChange={set("capacity")} min={1} max={20} tid="capacity-input" />
          <NumberField label="Minimum notice (hours)" value={s.min_notice_hours} onChange={set("min_notice_hours")} min={0} max={720} tid="min-notice-input" />
          <NumberField label="Booking horizon (days)" value={s.horizon_days} onChange={set("horizon_days")} min={1} max={365} tid="horizon-input" />
        </div>
      </Card>

      <Card title="Blocked dates" hint="Visitors cannot book on these dates.">
        <div className="flex gap-3">
          <input type="date" min={dubaiToday()} value={newBlock} onChange={(e) => setNewBlock(e.target.value)} className="field max-w-xs" data-testid="blocked-date-input" />
          <button onClick={addBlock} className="inline-flex items-center gap-2 rounded-full border bg-white px-4 text-sm font-medium" data-testid="blocked-date-add-button"><Plus size={14} /> Block</button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {s.blocked_dates.length === 0 && <p className="text-sm text-[#697386]">No blocked dates.</p>}
          {s.blocked_dates.map((d) => (
            <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-[#172033]" data-testid={`blocked-date-${d}`}>
              {ymdShort(d)}
              <button onClick={() => setS((p) => ({ ...p, blocked_dates: p.blocked_dates.filter((x) => x !== d) }))} aria-label={`Unblock ${d}`} data-testid={`blocked-date-remove-${d}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      </Card>

      <SpecificTimes s={s} setS={setS} />
    </div>
  );
}
